import { types, isStateTreeNode, getSnapshot } from 'mobx-state-tree';

/**
 * MST модель для глобальных UI состояний
 * Управляет только тем, что используется во всем приложении
 */

// Модель модального окна
const Modal = types
    .model('Modal', {
        id: types.identifier,
        type: types.enumeration(['edit', 'create', 'delete', 'confirm', 'info']),
        operation: types.maybe(types.string), // Операция: 'create', 'edit', 'delete', etc
        entityName: types.maybe(types.string), // 'user', 'project', etc
        entityId: types.maybe(types.string),
        targetId: types.maybe(types.string), // ID целевого объекта
        title: types.maybe(types.string),
        message: types.maybe(types.string),
        data: types.frozen(), // Данные для модалки
        parentContext: types.frozen() // Родительский контекст для наследования
        // Схема берется из PageContext, callback'и не нужны - логика в pageStore
    });

const UIStore = types
    .model('UIStore', {
        // Стек модальных окон для SmartModal
        modals: types.array(Modal),
        
        // Глобальная загрузка
        globalLoading: types.optional(types.boolean, false),
        globalLoadingText: types.maybe(types.string),
        
        // Тема приложения (для будущего использования)
        theme: types.optional(types.enumeration(['light', 'dark']), 'light'),
        
        // Состояние сайдбара (если будет добавлен)
        sidebarOpen: types.optional(types.boolean, true)
    })
    .views(self => ({
        // Есть ли открытые модалки
        get hasModals() {
            return self.modals.length > 0;
        },
        
        // Текущая активная модалка
        get currentModal() {
            return self.modals.length > 0 ? self.modals[self.modals.length - 1] : null;
        },
        
        // Проверка темной темы
        get isDarkTheme() {
            return self.theme === 'dark';
        }
    }))
    .actions(self => {
        // Приватные методы
        const saveSettings = () => {
            try {
                localStorage.setItem('ui-settings', JSON.stringify({
                    theme: self.theme,
                    sidebarOpen: self.sidebarOpen
                }));
            } catch (e) {
                console.error('Failed to save UI settings:', e);
            }
        };
        
        const loadSettings = () => {
            try {
                const saved = localStorage.getItem('ui-settings');
                if (saved) {
                    const settings = JSON.parse(saved);
                    if (settings.theme) self.theme = settings.theme;
                    if (settings.sidebarOpen !== undefined) self.sidebarOpen = settings.sidebarOpen;
                }
            } catch (e) {
                console.error('Failed to load UI settings:', e);
            }
        };
        
        return {
            // Управление модалками
            openModal(modalConfig) {
                // Определяем тип модалки на основе операции
                let modalType = modalConfig.type;
                if (!modalType && modalConfig.operation) {
                    const operationTypeMap = {
                        'create': 'create',
                        'edit': 'edit',
                        'update': 'edit',
                        'delete': 'delete',
                        'confirm': 'confirm'
                    };
                    modalType = operationTypeMap[modalConfig.operation] || 'info';
                }

                // Автоматически преобразуем MST объекты в обычные
                const plainData = modalConfig.data && isStateTreeNode(modalConfig.data)
                    ? getSnapshot(modalConfig.data)
                    : modalConfig.data;

                const plainParentContext = modalConfig.parentContext && isStateTreeNode(modalConfig.parentContext)
                    ? getSnapshot(modalConfig.parentContext)
                    : modalConfig.parentContext;

                const modal = Modal.create({
                    id: `modal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                    type: modalType || 'info',
                    operation: modalConfig.operation,
                    entityName: modalConfig.entityName || modalConfig.entity,
                    entityId: modalConfig.entityId,
                    targetId: modalConfig.targetId,
                    title: modalConfig.title,
                    message: modalConfig.message,
                    data: plainData || {},
                    parentContext: plainParentContext || null
                });
                
                self.modals.push(modal);
                return modal.id;
            },
            
            closeModal(modalId) {
                if (modalId) {
                    const index = self.modals.findIndex(m => m.id === modalId);
                    if (index !== -1) {
                        self.modals.splice(index, 1);
                    }
                } else {
                    // Закрыть последнюю модалку
                    self.modals.pop();
                }
            },
            
            closeAllModals() {
                self.modals.clear();
            },
            
            // Управление глобальной загрузкой
            showGlobalLoading(text) {
                self.globalLoading = true;
                self.globalLoadingText = text || null;
            },
            
            hideGlobalLoading() {
                self.globalLoading = false;
                self.globalLoadingText = null;
            },
            
            // Управление темой
            toggleTheme() {
                self.theme = self.theme === 'light' ? 'dark' : 'light';
                saveSettings();
            },
            
            setTheme(theme) {
                if (['light', 'dark'].includes(theme)) {
                    self.theme = theme;
                    saveSettings();
                }
            },
            
            // Управление сайдбаром
            toggleSidebar() {
                self.sidebarOpen = !self.sidebarOpen;
                saveSettings();
            },
            
            setSidebarOpen(open) {
                self.sidebarOpen = open;
                saveSettings();
            },
            
            // Lifecycle
            afterCreate() {
                loadSettings();
            },
            
            reset() {
                self.modals.clear();
                self.globalLoading = false;
                self.globalLoadingText = undefined;
                // Тему и сайдбар не сбрасываем - это настройки пользователя
            }
        };
    });

export { UIStore };